import React, { useEffect, useState, useCallback, useContext, useRef } from 'react'
import { Row, Col, Modal, Button } from 'react-bootstrap';
import { PaymentElement, useElements, useStripe, Elements } from "@stripe/react-stripe-js";
import _ from "lodash";
import { extractError } from 'api/ErrorUtil';
import { CheckoutContext } from '../CheckoutContext';
import { API } from 'api/API';
import { PAYMENTINTENT_TOKEN_STRIPE_URL } from 'config/config';
import { loadStripe } from "@stripe/stripe-js";
import { TenantContext } from "tenant/TenantContext";

const StripeForm = ({ paymentFunction, formData }) => {

    const { paymentDispatch, paymentData } = useContext(CheckoutContext)
    const [commitmentItems, setCommitmentItems] = useState({});
    const [stripePromise, setStripePromise] = useState(null);
    const [elements, setElements] = useState(null);
    const [initialPaymentIntent, setInitialPaymentIntent] = useState({});
    const { businessConfig } = useContext(TenantContext);

    function hasFrequency() {
        return formData.paymentCartItems.some(item => item.itemDetail?.frequency?.trim());
    }

    const getCommitmentItems = () => {
        const commitmentItems = formData.paymentCartItems.map(paymentCartItem => ({
            commitmentId: paymentCartItem.id,
            accountNumber: paymentCartItem?.account?.number,
            accountType: paymentCartItem?.accountType?.accountType,
            commitmentType: paymentCartItem.commitmentType,
            commitmentPaymentAmount: paymentCartItem.itemDetail.amountDue,
            paymentItems: paymentCartItem.itemDetail.lineItems,
            committmentFrequency: !_.isEmpty(paymentCartItem.itemDetail.frequency) ? paymentCartItem.itemDetail.frequency : null
        }));
        return commitmentItems;

    }

    const requestPaymentIntent = async () => {
        const commitmentItems = getCommitmentItems();
        let paymentIntentResponse = await API.post(PAYMENTINTENT_TOKEN_STRIPE_URL, {
            payer: formData.payer,
            commitmentItems: commitmentItems,
            customerConsentsToPayPfFees: formData.customerConsentsToPayPfFees,
            paymentSourceType: formData.paymentSourceType,
            paymentCardId: formData.paymentCardId,
            paymentCorrespodence: formData.paymentCorrespodence,
            receiptInCompanyName: formData.receiptInCompanyName,
            originalPaymentIntent: initialPaymentIntent
        });

        return paymentIntentResponse;

    }

    const makePaymentUsingSavedCC = async () => {
        console.log("Making payment using saved CC");
        try {
            let paymentResponse = await requestPaymentIntent();
            paymentDispatch({ type: 'PAYMENT_SUCCEEDED', payload: { paymentResponse: paymentResponse.data } });
            return;
        } catch (error) {
            let errors = extractError(error);
            paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: errors } });
            return
        }
    }

    const getPaymentIntent = useCallback(async () => {

        if (_.isEmpty(formData.paymentSourceType) || formData.paymentSourceType === 'OFFLINE' || formData.paymentCardId) {
            paymentFunction.current = makePaymentUsingSavedCC
            setElements(null);
            return;
        }

        if ((formData.paymentSourceType !== 'CARD') && (hasFrequency())) {
            alert("Recurring Payment can only be setup for Credit Cards. Payment will go through but recurring payment will not be setup for the selected payment type");

        }

        let localStripePromise = stripePromise;
        if (!localStripePromise) {
            localStripePromise = loadStripe(businessConfig.publishablekey);
            setStripePromise(localStripePromise);
        }

        let paymentIntentResponse = await requestPaymentIntent();


        const paymentElementOptions = {
            layout: "tabs",
            defaultValues: {
                billingDetails: {
                    name: formData.payer.firstName + " " + formData.payer.lastName,
                    email: formData.payer.email,
                    address: {
                        line1: formData.payer.address,
                        city: formData.payer.city,
                        state: formData.payer.province,
                        country: formData.payer.country,
                        postal_code: formData.payer.zip,
                    }
                }

            }
        };

        if (paymentIntentResponse) {
            setInitialPaymentIntent(paymentIntentResponse.data)
            setElements(
                <Elements stripe={localStripePromise} options={{ clientSecret: paymentIntentResponse.data.gatewayPaymentIntent.client_secret }}
                    key={paymentIntentResponse.data.gatewayPaymentIntent.client_secret}>
                    <PaymentForm options={paymentElementOptions} paymentFunction={paymentFunction} formData={formData} paymentIntent={paymentIntentResponse.data} />
                </Elements>
            );

        } else {
            setElements(null);
        }

    });

    useEffect(() => {
        getPaymentIntent().catch(err => {
            console.log(err);
            let errors = extractError(err);
            paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: errors } });
        });

    }, [formData.paymentSourceType, formData.paymentCardId])

    /*const setupPaymentForm = useCallback(async () => {
        const commitmentItems = getCommitmentItems();
        setCommitmentItems(commitmentItems);
    });

    useEffect(() => {
        setupPaymentForm().catch(err => {
            console.log(err);
            let errors = extractError(err);
            paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: errors } });
        });
    }, [paymentData]);*/

    const PaymentForm = ({ options, paymentFunction, formData, paymentIntent }) => { // Inner component to use hooks
        const stripe = useStripe();
        const elements = useElements();
        const [showModal, setShowModal] = useState(false);
        const [iframeUrl, setIframeUrl] = useState('');
        const [paymentElementComplete, setPaymentElementComplete] = useState();
        const modalResolveRef = useRef(null); // Reference to hold the resolve function of the promise

        const handleClose = async () => {
            const closePaymentIntent = await stripe.retrievePaymentIntent(paymentIntent.gatewayPaymentIntent.client_secret);
            if (closePaymentIntent?.error) {
                alert("Payment Processor Error:" + closePaymentIntent.error.message);
                return;

            }
            if (closePaymentIntent?.paymentIntent?.status && closePaymentIntent.paymentIntent.status !== 'requires_action') {
                setShowModal(false);
                if (modalResolveRef.current) {
                    modalResolveRef.current();  // Resolve the promise when the modal is closed
                    modalResolveRef.current = null;  // Reset the reference
                }
            } else {
                alert("Verification is not complete. Did you click on the Verify Button? If you have already done so, please wait for a minute and try again to close");
            }
        };

        const handlePaymentElementChange = async (event) => {
            setPaymentElementComplete(event.complete);
        };

        const handlePaymentResult = async (stripeResponse, paymentStatus) => {
            //At this time it means payment was approved. save payment result
            //update payment result in the database
            const commitmentItems = getCommitmentItems();
            let paymentUpdateResp = await API.put(PAYMENTINTENT_TOKEN_STRIPE_URL, {
                gatewayResponse: stripeResponse,
                paymentStatus: paymentStatus,
                payer: formData.payer,
                commitmentItems: commitmentItems,
                paymentIntentId: paymentIntent.paymentIntentId,
                customerConsentsToPayPfFees: formData.customerConsentsToPayPfFees,
                paymentSourceType: formData.paymentSourceType,
                paymentCorrespodence: formData.paymentCorrespodence,
                receiptInCompanyName: formData.receiptInCompanyName
            });
            return paymentUpdateResp;
        }

        const handleNextAction = (paymentIntent) => {
            const { next_action } = paymentIntent;

            const url =
                next_action.verify_with_microdeposits?.hosted_verification_url ||
                next_action.redirect_to_url?.url ||
                next_action.verify_with_sms?.url;

            if (url) {
                setIframeUrl(url);
                setShowModal(true);

                // Return a promise that resolves when the modal is closed
                return new Promise((resolve) => {
                    modalResolveRef.current = resolve;
                });
            } else {
                paymentDispatch({
                    type: 'PAYMENT_ERROR',
                    payload: { transactionToken: paymentIntent, errors: { message: `Unhandled next_action: ${next_action.type}` } }
                });
                return Promise.resolve();
            }
        };

        const makePayment = async (event) => {
            let stripeResponse = null;
            try {

                if (_.isEmpty(paymentIntent)) {
                    alert("Payment has not been initialized properly. please reselect a payment source type");
                    return;
                }

                if (!_.isEmpty(formData.paymentCardId)) {
                    //this means that payment was via a token
                    paymentDispatch({ type: 'PAYMENT_SUCCEEDED', payload: { paymentResponse: paymentIntent } });
                    return;
                }

                if (!paymentElementComplete) {
                    paymentDispatch({ type: 'PAYMENT_CARD_VALIDATION_ERROR', payload: { transactionToken: paymentIntent, errors: { message: "Payment Card information is invalid" } } });
                    return;
                }

                // Trigger form validation and wallet collection
                const { error: submitError } = await elements.submit();
                if (submitError) {
                    paymentDispatch({ type: 'PAYMENT_CARD_VALIDATION_ERROR', payload: { transactionToken: paymentIntent, errors: { message: "Payment Card information is invalid" } } });
                    return;
                }

                let pmtElement = elements.getElement(PaymentElement);
                pmtElement.update({ readOnly: true });
                let errorObj = null;
                let returnLocation = window.location.href;

                //stripe will return to return_url upon success
                stripeResponse = await stripe.confirmPayment({
                    elements: elements,
                    clientSecret: paymentIntent.gatewayPaymentIntent.client_secret,
                    confirmParams: {
                        return_url: returnLocation + "?pmtresult=true",
                    },
                    redirect: "if_required"
                });


                let paymentUpdateResp = {};
                if (stripeResponse.error) {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse, 'PAYMENT_ERROR');
                    paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: { message: "Payment Error Code:" + stripeResponse.error.code + " " + stripeResponse.error.message } } });
                    pmtElement.update({ readOnly: false });
                    return;
                }
                if (stripeResponse.paymentIntent.status === 'requires_action') {
                    paymentDispatch({ type: 'PAYMENT_WORKING_SECONDARY', payload: { errors: { message: "Please complete required information in the popup" } } });
                    if (stripeResponse.paymentIntent.next_action) {
                        await handleNextAction(stripeResponse.paymentIntent);
                    }
                    paymentUpdateResp = await handlePaymentResult(stripeResponse.paymentIntent, 'PAYMENT_PROCESSING');
                    paymentDispatch({ type: 'PAYMENT_PROCESSING', payload: { paymentResponse: paymentUpdateResp.data } });
                    return;
                } else if (stripeResponse.paymentIntent.status === 'processing') {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse.paymentIntent, 'PAYMENT_PROCESSING');
                    paymentDispatch({ type: 'PAYMENT_PROCESSING', payload: { paymentResponse: paymentUpdateResp.data } });
                    return;
                } else if (stripeResponse.paymentIntent.status === 'requires_payment_method') {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse, 'PAYMENT_DECLINED');
                    paymentDispatch({ type: 'PAYMENT_DECLINED', payload: { errors: { message: "Payment method was declined" } } });
                    pmtElement.update({ readOnly: false });
                    return;
                } else if (stripeResponse.paymentIntent.status === 'succeeded') {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse.paymentIntent, 'PAYMENT_SUCCEEDED');
                    paymentDispatch({ type: 'PAYMENT_SUCCEEDED', payload: { paymentResponse: paymentUpdateResp.data } });
                    return;
                } else if (stripeResponse.paymentIntent.status === 'canceled') {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse, 'PAYMENT_CANCELLED');
                    paymentDispatch({ type: 'PAYMENT_CANCELLED', payload: { errors: { message: "Payment was cancelled" } } });
                    return;
                } else {
                    paymentUpdateResp = await handlePaymentResult(stripeResponse, 'PAYMENT_ERROR');
                    paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: { message: "Unnown error in payment gateway" } } });
                    pmtElement.update({ readOnly: false });
                    return;
                }

            } catch (error) {
                let errorObj = extractError(error);
                if (stripeResponse?.paymentIntent?.status === 'succeeded') {
                    errorObj.message = "Payment was successful but could not be saved. Please contact admin. Error:" + errorObj.message;
                }
                paymentDispatch({ type: 'PAYMENT_ERROR', payload: { errors: errorObj } });
            }

        }
        useEffect(() => {
            paymentFunction.current = makePayment
        }, [makePayment]);

        return (<>
            <PaymentElement options={options} onChange={handlePaymentElementChange} />
            <Modal show={showModal} onHide={handleClose}
                backdrop="static"
                keyboard={false}
                className='custom-modal'
                >
                <Modal.Header closeButton/>
                <Modal.Body>
                    <div className='iframe-content'>
                        <iframe
                            src={iframeUrl}
                            title="Payment Verification"
                            className='iframe-content'
                        />
                    </div>
                </Modal.Body>
            </Modal>
        </>
        );

    };

    return (
        <>
            {stripePromise && elements &&
                <>
                    <Row><Col>
                        {elements}
                    </Col></Row>
                    <Row><Col>&nbsp;</Col></Row>

                </>}

        </>
    )
}

export default StripeForm